import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Video, Tag } from '../types/video';
import { ArrowLeft, Save, Plus, X, Download } from 'lucide-react';

export default function AdminVideoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [showNewTag, setShowNewTag] = useState(false);

  const [formData, setFormData] = useState({
    adilo_url: '',
    title: '',
    description: '',
    thumbnail_url: '',
    duration: 0,
    is_published: true,
    is_featured: false,
    sort_order: 0,
  });

  useEffect(() => {
    loadTags();
    if (isEdit) {
      loadVideo();
    }
  }, [id]);

  async function loadTags() {
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');

      if (error) throw error;
      setAllTags(data || []);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  }

  async function loadVideo() {
    try {
      const { data: videoData, error: videoError } = await supabase
        .from('videos')
        .select('*')
        .eq('id', id)
        .single();

      if (videoError) throw videoError;

      setFormData({
        adilo_url: videoData.adilo_url,
        title: videoData.title,
        description: videoData.description,
        thumbnail_url: videoData.thumbnail_url,
        duration: videoData.duration,
        is_published: videoData.is_published,
        is_featured: videoData.is_featured,
        sort_order: videoData.sort_order,
      });

      const { data: tagData } = await supabase
        .from('video_tags')
        .select('tag_id')
        .eq('video_id', id);

      setSelectedTags(tagData?.map((vt) => vt.tag_id) || []);
    } catch (error) {
      console.error('Error loading video:', error);
    }
  }

  async function createTag() {
    if (!newTagName.trim()) return;

    try {
      const slug = newTagName.toLowerCase().replace(/\s+/g, '-');
      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
      const color = colors[Math.floor(Math.random() * colors.length)];

      const { data, error } = await supabase
        .from('tags')
        .insert({ name: newTagName, slug, color })
        .select()
        .single();

      if (error) throw error;

      setAllTags([...allTags, data]);
      setSelectedTags([...selectedTags, data.id]);
      setNewTagName('');
      setShowNewTag(false);
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  }

  async function autoFetchThumbnail() {
    if (!formData.adilo_url) {
      alert('Please enter an Adilo URL first');
      return;
    }

    setLoading(true);
    try {
      const videoId = extractAdiloVideoId(formData.adilo_url);
      if (!videoId) {
        alert('Could not extract video ID from Adilo URL');
        return;
      }

      const possibleThumbnailUrls = [
        `https://adilo.bigcommand.com/thumbnail/${videoId}.jpg`,
        `https://adilo.bigcommand.com/thumbnail/${videoId}.png`,
        `https://adilo.bigcommand.com/thumbnails/${videoId}.jpg`,
        `https://adilo.bigcommand.com/thumbnails/${videoId}.png`,
        `https://cdn.adilo.com/thumbnails/${videoId}.jpg`,
        `https://cdn.adilo.com/thumbnail/${videoId}.jpg`,
      ];

      for (const url of possibleThumbnailUrls) {
        try {
          const response = await fetch(url, { method: 'HEAD' });
          if (response.ok) {
            setFormData({ ...formData, thumbnail_url: url });
            return;
          }
        } catch (err) {
          continue;
        }
      }

      alert('Could not automatically find thumbnail. Please enter the thumbnail URL manually from your Adilo dashboard.');
    } catch (error) {
      console.error('Error fetching thumbnail:', error);
      alert('Failed to fetch thumbnail automatically');
    } finally {
      setLoading(false);
    }
  }

  function extractAdiloVideoId(url: string): string | null {
    try {
      const patterns = [
        /adilo\.bigcommand\.com\/watch\/([^/?#]+)/,
        /adilo\.bigcommand\.com\/embed\/([^/?#]+)/,
        /adilo\.bigcommand\.com\/video\/([^/?#]+)/,
        /adilo\.bigcommand\.com\/v\/([^/?#]+)/,
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }

      return null;
    } catch {
      return null;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let videoId = id;

      if (isEdit) {
        const { error } = await supabase
          .from('videos')
          .update(formData)
          .eq('id', id);

        if (error) throw error;

        await supabase.from('video_tags').delete().eq('video_id', id);
      } else {
        const { data: maxSortOrder } = await supabase
          .from('videos')
          .select('sort_order')
          .order('sort_order', { ascending: false })
          .limit(1)
          .maybeSingle();

        const nextSortOrder = (maxSortOrder?.sort_order ?? -1) + 1;

        const { data, error } = await supabase
          .from('videos')
          .insert({ ...formData, user_id: user.id, sort_order: nextSortOrder })
          .select()
          .single();

        if (error) throw error;
        videoId = data.id;
      }

      if (selectedTags.length > 0) {
        const videoTags = selectedTags.map((tagId) => ({
          video_id: videoId,
          tag_id: tagId,
        }));

        const { error: tagError } = await supabase
          .from('video_tags')
          .insert(videoTags);

        if (tagError) throw tagError;
      }

      navigate('/dashboard/admin/videos');
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Failed to save video. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function toggleTag(tagId: string) {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/dashboard/admin/videos')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Videos</span>
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            {isEdit ? 'Edit Video' : 'Add New Video'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adilo Video URL *
              </label>
              <input
                type="url"
                required
                value={formData.adilo_url}
                onChange={(e) => setFormData({ ...formData, adilo_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://..."
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter the embed URL from Adilo
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Video title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the video content..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Thumbnail URL
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://..."
                />
                <button
                  type="button"
                  onClick={autoFetchThumbnail}
                  disabled={loading || !formData.adilo_url}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Auto-fetch thumbnail from Adilo"
                >
                  <Download size={20} />
                  Fetch from Adilo
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Click "Fetch from Adilo" to automatically get the thumbnail, or paste the URL manually
              </p>
              {formData.thumbnail_url && (
                <img
                  src={formData.thumbnail_url}
                  alt="Preview"
                  className="mt-2 w-48 h-32 object-cover rounded"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (seconds)
              </label>
              <input
                type="number"
                min="0"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {allTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                      selectedTags.includes(tag.id)
                        ? 'ring-2 ring-offset-2'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                      ringColor: tag.color,
                    }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>

              {showNewTag ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="New tag name"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={createTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewTag(false);
                      setNewTagName('');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    <X size={20} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewTag(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Create New Tag
                </button>
              )}
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Published</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Featured</span>
              </label>
            </div>

            <div className="flex gap-4 pt-6">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md disabled:opacity-50"
              >
                <Save size={20} />
                {loading ? 'Saving...' : 'Save Video'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard/admin/videos')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
